import React, { useContext, useState } from 'react';
import { Box, Grid, IconButton, Menu, MenuItem, Paper, Skeleton, Typography } from 'housecall-ui';
import InfoChip from 'housecall-ui/dist/DesignSystem/DataDisplay/InfoChip/InfoChip';
import MoreVert from 'housecall-ui/dist/IconsAndImages/Icons/MoreVert';
import moment from 'moment';
import type { ValueOf } from 'type-fest';
import { AmplitudeFlagKeys } from 'types/amplitudeFlags';

import { OrganizationContext } from 'components/AdminNew/Companies/CompanyDetails/OrganizationContext';
import {
  PERMISSION_CHARGE_ORGANIZATION,
  PERMISSION_READ_ONLY_CHARGE_ORGANIZATION,
} from 'components/AdminNew/HousecallEmployees/Permissions';
import { usePermissions } from 'components/AdminNew/HousecallEmployees/usePermissions';
import useIsExperimentActive from 'components/Amplitude/useIsExperimentActive';

import { CreditCardItemData } from '../PaymentMethod';
import { TriggerContext } from '../types';

export const isNotCreditCardExpired = (expirationDate: string) =>
  moment().startOf('month').isSameOrBefore(moment(expirationDate, 'MM/YY'));

export const CreditCardProvider = {
  Visa: 'Visa',
  MasterCard: 'MasterCard',
  Maestro: 'Maestro',
  AmericanExpress: 'American Express',
  DinersClub: 'Diners',
  Discover: 'Discover',
  JCB: 'JCB',
};

export type CreditCardItemProps = CreditCardItemData & {
  primary?: boolean;
  onChargeCardMenuItemClick: (id: number | undefined) => void;
  onRemoveCardMenuItemClick: (id: number | string, cardName: string) => void;
  onUpdateCardMenuItemClick: (creditCardData: CreditCardItemData) => void;
  onSetPrimaryCardMenuItemClick: (id: number | string, cardName: string) => void;
};

export const getCreditCardIconImage = (provider: ValueOf<typeof CreditCardProvider> | undefined) => {
  switch (provider) {
    case CreditCardProvider.Visa:
      return '/assets/CreditCardPaymentIcons/VisaIcon.svg';
    case CreditCardProvider.MasterCard:
      return '/assets/CreditCardPaymentIcons/MasterCardNewIcon.svg';
    case CreditCardProvider.Maestro:
      return '/assets/CreditCardPaymentIcons/MaestroIcon.svg';
    case CreditCardProvider.AmericanExpress:
    case 'Amex':
      return '/assets/CreditCardPaymentIcons/AmexIcon.svg';
    case CreditCardProvider.DinersClub:
      return '/assets/CreditCardPaymentIcons/DinersClubIcon.svg';
    case CreditCardProvider.Discover:
      return '/assets/CreditCardPaymentIcons/DiscoverIcon.svg';
    case CreditCardProvider.JCB:
      return '/assets/CreditCardPaymentIcons/JCBIcon.svg';
    default:
      return '/assets/CreditCardPaymentIcons/GenericCardIcon.svg';
  }
};

export const CreditCardItemSkeleton = ({ elevate }: { elevate: boolean }) => {
  return (
    <Paper elevation={elevate ? 3 : 0}>
      <Box px={2} py={3} my={1}>
        <Grid container spacing={3} direction="row" alignContent="center" alignItems="center">
          <Grid item>
            <Box width={48}>
              <Skeleton variant="rect" width={48} height={48} />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="caption">
              <Skeleton variant="text" width={60} />
            </Typography>
            <Typography variant="body2">
              <Skeleton variant="text" width={60} />
            </Typography>
          </Grid>
          <Grid item xs>
            <Typography variant="caption">
              <Skeleton variant="text" width={80} />
            </Typography>
            <Typography variant="body2">
              <Skeleton variant="text" width={50} />
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const CreditCardItem = ({
  id,
  uuid,
  primary = false,
  cardNumber,
  provider,
  expirationDate,
  nameOnCard,
  zip,
  onChargeCardMenuItemClick,
  onRemoveCardMenuItemClick,
  onUpdateCardMenuItemClick,
  onSetPrimaryCardMenuItemClick,
}: CreditCardItemProps) => {
  const { triggerContext: context } = useContext(OrganizationContext);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const useCreditCardsUUIDs = useIsExperimentActive(AmplitudeFlagKeys.billingEnableCreditCardsUUIDs);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChargeCard = () => {
    onChargeCardMenuItemClick(id);
    setAnchorEl(null);
  };

  const handleUpdateCreditCard = () => {
    if (id && uuid) {
      onUpdateCardMenuItemClick({
        cardNumber,
        provider,
        id,
        uuid,
        expirationDate,
        nameOnCard,
        zip,
      });
      setAnchorEl(null);
    }
  };

  const handleRemoveCreditCard = () => {
    const cardId = useCreditCardsUUIDs ? uuid : id;

    if (cardId) {
      onRemoveCardMenuItemClick(cardId, `${provider} ${cardNumber}`);
      setAnchorEl(null);
    }
  };

  const handleSetPrimaryCreditCard = () => {
    const cardId = useCreditCardsUUIDs ? uuid : id;

    if (cardId) {
      onSetPrimaryCardMenuItemClick(cardId, `${provider} ${cardNumber}`);
      setAnchorEl(null);
    }
  };

  const isNotExpired = isNotCreditCardExpired(expirationDate);

  const isAuthorizedToManageCharge = usePermissions(PERMISSION_CHARGE_ORGANIZATION);
  const isAuthorizedToReadCharge = usePermissions(PERMISSION_READ_ONLY_CHARGE_ORGANIZATION);
  const canViewChargeModal = isAuthorizedToManageCharge || isAuthorizedToReadCharge;

  const menuItems = [];

  menuItems.push(
    <MenuItem
      key="credit-card-item__set-as-primary"
      onClick={handleSetPrimaryCreditCard}
      disabled={primary || !isNotExpired}
      data-testid="credit-card-item__menu-item"
    >
      Set as primary
    </MenuItem>
  );

  menuItems.push(
    <MenuItem key="credit-card-item__update" onClick={handleUpdateCreditCard} data-testid="credit-card-item__menu-item">
      Update card
    </MenuItem>
  );

  menuItems.push(
    <MenuItem
      key="credit-card-item__remove"
      onClick={handleRemoveCreditCard}
      disabled={primary}
      data-testid="credit-card-item__menu-item"
    >
      Remove card
    </MenuItem>
  );

  if (context === TriggerContext.AdminPanel) {
    menuItems.push(
      <MenuItem
        key="credit-card-item__charge"
        disabled={!canViewChargeModal}
        onClick={handleChargeCard}
        data-testid="credit-card-item__menu-item"
      >
        Charge card
      </MenuItem>
    );
  }

  return (
    <Paper elevation={primary ? 3 : 0} data-testid="credit-card-item">
      <Box px={2} py={3} my={1}>
        <Grid container spacing={3} direction="row" alignContent="center" alignItems="center">
          <Grid item>
            <Box width={48}>
              <img
                width="100%"
                src={getCreditCardIconImage(provider)}
                alt={provider}
                data-testid="credit-card-item__provider-logo"
              />
            </Box>
          </Grid>
          <Grid item>
            <Typography variant="caption">Credit card</Typography>
            <Typography variant="body2">{cardNumber}</Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="caption"
              color={isNotExpired ? 'initial' : 'error'}
              data-testid="credit-card-item__expiration-label"
            >
              {isNotExpired ? 'Expiration date' : 'Expired on'}
            </Typography>
            <Typography variant="body2">{expirationDate}</Typography>
          </Grid>
          <Grid item xs>
            {primary && <InfoChip color="primary" label="Primary" data-testid="credit-card-item__primary-badge" />}
          </Grid>
          <Grid item>
            {menuItems.length > 0 && (
              <>
                <IconButton
                  aria-controls="credit-card-menu"
                  aria-haspopup="true"
                  onClick={handleMenuClick}
                  data-testid="credit-card-item__menu"
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  id="credit-card-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {menuItems}
                </Menu>
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default CreditCardItem;
